/* eslint-disable max-lines-per-function */
import { useCallback, useMemo } from 'react';

import { HealthcareProviderLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';
import { ReceptionServiceWithPractitionersSimple } from '@dreryk/egabinet-private-medical-service-frontend-api';

import { commonTranslationKeys, createTranslatedText, useFormController } from '@dreryk/react-common';
import { DatePicker, Form, Select, StyledFlex } from '@dreryk/react-components';

import { NoteTextarea } from '~libs.desktop.shared.appointment';
import { protectFeature } from '~libs.shared.system';

import { prescriptionVisitsTranslationKeys } from '../../translations';
import { HealthcareProviderLocationSelect } from '../../calendar/appointment/shared/form/fields/healthcareProviderLocation/HealthcareProviderLocationSelect';
import { HealthcareProviderLocationTextView } from '../../calendar/appointment/shared/form/fields/healthcareProviderLocation/HealthcareProviderLocationTextView';
import { PatientAutosuggest } from '../../calendar/appointment/shared/form/fields/patient/PatientAutosuggest';
import { TimeSinceLastVisitInformation } from '../actionBar/TimeSinceLastVisitInformation';

import { useAutoResolveReceptionServiceFromTemplate } from './PrescriptionVisitForm.hooks';
import { PrescriptionVisitFormBean, PrescriptionVisitFormController } from './PrescriptionVisitForm.types';
import { PrescriptionVisitPractitionerTextView } from './PrescriptionVisitPractitionerTextView';
import {
   doctorSimpleLabelExtractor,
   filterReceptionServicesByPractitioner,
   nameLabelExtractor,
   prescriptionHealthcareProviderLocationViewVisitor,
   prescriptionPractitionerViewVisitor,
   resolveReceptionServiceFromTemplate,
} from './PrescriptionVisitForm.utils';

type PrescriptionVisitFormRenderProps = Pick<PrescriptionVisitFormController, 'handleSubmit' | 'formState'>;

type PrescriptionVisitFormProps = {
   readonly initializer: () => PrescriptionVisitFormBean;
   readonly practitioners: DoctorSimple[];
   readonly healthcareProviderLocations: HealthcareProviderLocation[];
   readonly receptionServices: ReceptionServiceWithPractitionersSimple[];
   readonly shouldAutoResolveReceptionService?: boolean;
   readonly children: (renderProps: PrescriptionVisitFormRenderProps) => JSX.Element;
};

export const PrescriptionVisitForm = (props: PrescriptionVisitFormProps): JSX.Element => {
   const { register, handleSubmit, setValue, formState, watch } = useFormController<PrescriptionVisitFormBean>(
      props.initializer()
   );

   const {
      healthcareProviderLocation: selectedHealthcareProviderLocation,
      practitioner: selectedPractitioner,
   } = watch(['healthcareProviderLocation', 'practitioner']);

   const privateMedicalServicesAvailable = protectFeature('searchingPrivateMedicalServices', true, false);

   const availableReceptionServices = useMemo(
      () => filterReceptionServicesByPractitioner(props.receptionServices, selectedPractitioner),
      [props.receptionServices, selectedPractitioner]
   );

   const handlePractitionerChange = useCallback(
      async (practitioner: DoctorSimple | null) => {
         const service = practitioner
            ? await resolveReceptionServiceFromTemplate(practitioner.practitionerId, props.receptionServices)
            : null;
         setValue('receptionService', service);
      },
      [props.receptionServices, setValue]
   );

   useAutoResolveReceptionServiceFromTemplate({
      enabled: privateMedicalServicesAvailable && (props.shouldAutoResolveReceptionService ?? false),
      practitioners: props.practitioners,
      receptionServices: props.receptionServices,
      watch: watch,
      setValue: setValue,
   });

   return (
      <Form noValidate>
         <StyledFlex direction={'column'} gap={16}>
            {prescriptionHealthcareProviderLocationViewVisitor(
               props.healthcareProviderLocations,
               selectedHealthcareProviderLocation,
               formState.touched.healthcareProviderLocation
            )({
               text: ({ editable }) => <HealthcareProviderLocationTextView register={register} editable={editable} />,
               select: () => (
                  <HealthcareProviderLocationSelect
                     register={register}
                     healthcareProviderLocations={props.healthcareProviderLocations}
                  />
               ),
            })}

            {prescriptionPractitionerViewVisitor(
               props.practitioners,
               selectedPractitioner,
               formState.touched.practitioner
            )({
               text: () => <PrescriptionVisitPractitionerTextView register={register} />,
               select: () => (
                  <Select<DoctorSimple>
                     triggerLabelExtractor={doctorSimpleLabelExtractor}
                     controller={register('practitioner', { required: true })}
                     label={prescriptionVisitsTranslationKeys.form.practitioner}
                     placeholder={commonTranslationKeys.choose}
                     testId={'practitioner'}
                     searchInputEnabled
                     onSelect={privateMedicalServicesAvailable ? handlePractitionerChange : undefined}
                  >
                     <Select.List items={props.practitioners} keyExtractor={'practitionerId'}>
                        {(item: DoctorSimple) => (
                           <Select.Item
                              item={item}
                              testId={String(item.practitionerId)}
                              label={createTranslatedText(`${item.lastName} ${item.firstName}`)}
                           />
                        )}
                     </Select.List>
                  </Select>
               ),
            })}

            <PatientAutosuggest register={register} name={'patient'} disabled={false} />

            <TimeSinceLastVisitInformation watch={watch} />

            <DatePicker
               controller={register('date', { required: true })}
               testId={'date'}
               label={prescriptionVisitsTranslationKeys.form.date}
            />

            <NoteTextarea register={register} label={prescriptionVisitsTranslationKeys.form.note} />

            {privateMedicalServicesAvailable ? (
               <Select<ReceptionServiceWithPractitionersSimple>
                  triggerLabelExtractor={nameLabelExtractor}
                  controller={register('receptionService')}
                  label={prescriptionVisitsTranslationKeys.form.receptionService}
                  placeholder={commonTranslationKeys.choose}
                  testId={'receptionService'}
                  showClearIcon
               >
                  <Select.List items={availableReceptionServices} keyExtractor={'id'}>
                     {(item: ReceptionServiceWithPractitionersSimple) => (
                        <Select.Item item={item} testId={item.name} label={createTranslatedText(item.name)} />
                     )}
                  </Select.List>
               </Select>
            ) : null}
         </StyledFlex>

         {props.children({ handleSubmit: handleSubmit, formState: formState })}
      </Form>
   );
};
